import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/widgets";
import { severityColor } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ScanLine, Camera, Loader2, AlertTriangle, Wrench, Gauge, Disc, Zap, Droplets, Car, Battery, Lightbulb, ChevronRight, Crown,
} from "lucide-react";

const CATEGORIES = [
  { id: "dashboard warning lights", label: "Dashboard Lights", icon: Lightbulb },
  { id: "engine bay", label: "Engine Bay", icon: Gauge },
  { id: "tyres", label: "Tyres", icon: Disc },
  { id: "brake components", label: "Brakes", icon: Disc },
  { id: "exterior damage", label: "Exterior Damage", icon: Car },
  { id: "battery", label: "Battery", icon: Battery },
  { id: "fluids", label: "Fluids", icon: Droplets },
];

function resizeImage(file, maxDim = 1024) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) { height = (height * maxDim) / width; width = maxDim; }
        else if (height > maxDim) { width = (width * maxDim) / height; height = maxDim; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Scan() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [category, setCategory] = useState(null);
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/scans").then((r) => setHistory(r.data)).catch(() => {}); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = await resizeImage(file);
    setB64(data);
    setPreview(data);
    setResult(null);
  };

  const runScan = async () => {
    if (!category || !b64) { toast.error("Pick a category and add a photo."); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/scan", { category, image_base64: b64 });
      setResult(data.result);
      api.get("/scans").then((r) => setHistory(r.data));
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("Free scan limit reached.");
        navigate("/app/premium");
      } else toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setPreview(null); setB64(null); setResult(null); setCategory(null); };

  return (
    <div className="space-y-6" data-testid="scan-page">
      <PageHeader title="AI Car Scan" subtitle="Snap a photo. Know the problem." icon={ScanLine} />

      {user?.plan !== "premium" && user?.role !== "admin" && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <Crown className="text-amber-600 shrink-0" size={20} />
          <p className="text-sm text-amber-800 flex-1">Free plan includes 2 scans. <button onClick={() => navigate("/app/premium")} className="font-semibold underline">Go Premium</button> for unlimited.</p>
        </div>
      )}

      {/* Category picker */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-zinc-700">What do you want to check?</h2>
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {CATEGORIES.map((c) => (
            <button key={c.id} data-testid={`scan-cat-${c.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setCategory(c.id)}
              className={`flex flex-col items-center gap-2 rounded-2xl border p-4 text-center transition-colors ${
                category === c.id ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 bg-white hover:border-emerald-300"}`}>
              <c.icon size={22} className={category === c.id ? "text-emerald-600" : "text-zinc-400"} />
              <span className="text-[11px] font-medium leading-tight text-zinc-700">{c.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Upload */}
      <div className="rounded-3xl border border-zinc-200 bg-white p-5">
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="scan-file-input" />
        {preview ? (
          <div>
            <img src={preview} alt="preview" className="h-56 w-full rounded-2xl object-cover" />
            <div className="mt-4 flex gap-3">
              <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-full">Retake</Button>
              <Button data-testid="scan-analyze" onClick={runScan} disabled={loading || !category}
                className="flex-1 rounded-full bg-emerald-500 hover:bg-emerald-600">
                {loading ? <><Loader2 className="animate-spin mr-2" size={18} /> Scanning…</> : "Analyze with AI"}
              </Button>
            </div>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()} data-testid="scan-upload-trigger"
            className="flex w-full flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-zinc-300 py-12 transition-colors hover:border-emerald-400 hover:bg-emerald-50/40">
            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-emerald-50 text-emerald-600"><Camera size={26} /></div>
            <div className="text-sm font-medium text-zinc-700">Tap to add a photo</div>
            <div className="text-xs text-zinc-400">{category ? "Now snap the photo" : "First pick a category above"}</div>
          </button>
        )}
      </div>

      {/* Result */}
      {result && (
        <div className="rounded-3xl border border-zinc-200 bg-white p-6 vp-fade-up" data-testid="scan-result">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-bold">Scan Result</h3>
            <span className={`rounded-full border px-3 py-1 text-xs font-bold ${severityColor[result.severity] || severityColor.Low}`}>
              {result.severity}
            </span>
          </div>
          <p className="mt-3 rounded-2xl bg-emerald-50 p-4 text-sm text-emerald-800">{result.summary}</p>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex gap-3"><dt className="w-28 shrink-0 text-zinc-400">Likely issue</dt><dd className="font-medium text-zinc-800">{result.problem}</dd></div>
            <div className="flex gap-3"><dt className="w-28 shrink-0 text-zinc-400">Action</dt><dd className="text-zinc-700">{result.action}</dd></div>
            <div className="flex gap-3"><dt className="w-28 shrink-0 text-zinc-400">Urgency</dt><dd className="text-zinc-700">{result.urgency}</dd></div>
            <div className="flex gap-3"><dt className="w-28 shrink-0 text-zinc-400">Confidence</dt><dd className="text-zinc-700">{result.confidence}</dd></div>
          </dl>
          <div className="mt-5 flex gap-3">
            <Button onClick={reset} variant="outline" className="rounded-full">New Scan</Button>
            <Button data-testid="scan-mechanic" className="flex-1 rounded-full bg-zinc-900 hover:bg-zinc-800 gap-2">
              <Wrench size={16} /> See a mechanic
            </Button>
          </div>
          <div className="mt-4 flex items-start gap-2 rounded-2xl bg-zinc-50 p-3 text-xs text-zinc-500">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            Visual AI is an advisory tool and does not replace professional diagnosis.
          </div>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div>
          <h2 className="mb-3 font-display text-lg font-semibold">Recent scans</h2>
          <div className="space-y-2">
            {history.slice(0, 5).map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-2xl border border-zinc-200 bg-white p-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium capitalize text-zinc-800">{s.category}</div>
                  <div className="truncate text-xs text-zinc-400">{s.result?.problem}</div>
                </div>
                <span className={`ml-3 shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${severityColor[s.result?.severity] || severityColor.Low}`}>{s.result?.severity}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
